import type { ButtonProps } from "@evidenceb/athena-common/design-system/Button";
import { Modal } from "@evidenceb/athena-common/design-system/Modal";
import pRetry from "p-retry";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useIdleTimer } from "react-idle-timer";
import { defineMessages, useIntl } from "react-intl";
import { UserType } from "../interfaces/User";
import { redirectAfterLogout } from "../utils/auth";
import { axiosClient } from "../utils/axios-client/axios-client";
import * as localStorage from "../utils/localStorage";
import { configStore } from "./ConfigContext";
import { useInitializingEventStream } from "./EventStreamContext";
import { sessionStore } from "./SessionContext";

/**
 * Reason the guard popup is showing:
 * - `idle`: the user has been inactive for {@link TIMEOUT} — recoverable by
 *   confirming they are still here.
 * - `expired`: the session token can no longer be renewed (the axios kill
 *   switch turned on) — recoverable only by a fresh token refresh, else login.
 */
type GuardReason = "idle" | "expired";

const TIMEOUT = 5 * 60 * 1000;

interface SessionGuardValue {
  reason: GuardReason | undefined;
  /**
   * @param hold
   * @returns
   */
  setExpiredHold: (hold: boolean) => void;
  isBlocked: () => boolean;
}

const SessionGuardContext = createContext<SessionGuardValue>({
  reason: undefined,
  setExpiredHold: () => {},
  isBlocked: () => false,
});

/**
 * App-wide guard shown for both players and teachers. Opens a single popup
 * when the user goes idle or when the session token dies, and while it is up
 * pauses event sync (see {@link useInitializingEventStream}'s `setPaused`).
 */

export function SessionGuardProvider({ children }: PropsWithChildren) {
  const intl = useIntl();
  const { config } = useContext(configStore);
  const {
    session: { userId, appProvider, userType },
  } = useContext(sessionStore);
  const athenaEventStream = useInitializingEventStream();

  const [reason, setReason] = useState<GuardReason | undefined>(undefined);
  const [sessionDead, setSessionDead] = useState(false);
  const [expiredHold, setExpiredHold] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectFailed, setReconnectFailed] = useState(false);

  const timer = useIdleTimer({
    timeout: getIdleTimeout(),
    startOnMount: true,
    onIdle: () => {
      // No idle popup before login (regardless of any stale token). `expired`
      // is more severe, so never let idle overwrite it.
      if (userId) setReason((prev) => prev ?? "idle");
    },
  });

  // Clears the session and sends the user to login (the "Se déconnecter"
  // action of the expired popup).
  const goToLogin = useCallback(() => {
    redirectAfterLogout(config, appProvider);
  }, [config, appProvider]);

  // The session token can no longer be renewed.
  useEffect(() => {
    axiosClient.onSessionDead(() => {
      if (userId) {
        // Logged-in session died: offer the reconnect popup.
        setSessionDead(true);
      } else {
        // If we are not logged in, something was likely wrong with the token. Just logout altogether
        localStorage.removeItem(localStorage.Key.TOKEN);
      }
    });
  }, [userId]);

  useEffect(() => {
    if (sessionDead && !expiredHold) setReason("expired");
  }, [sessionDead, expiredHold]);

  const isBlocked = reason !== undefined;

  // Pause pushing events while blocked; publishing keeps queueing locally
  // (offline-parity) so nothing is lost, and sync resumes on close.
  useEffect(() => {
    athenaEventStream?.setPaused(isBlocked);
  }, [athenaEventStream, isBlocked]);

  // Deterministic e2e handle.
  useSessionGuardTestApi(reason, () => {
    if (userId) setReason((prev) => prev ?? "idle");
  });

  const close = () => {
    timer.reset();
    timer.start();
    setReason(undefined);
    setReconnectFailed(false);
  };

  const onReconnect = async () => {
    setReconnecting(true);
    setReconnectFailed(false);
    try {
      // Retry silently before anything.
      await pRetry(() => axiosClient.getAndUpdateToken(), {
        retries: 3,
        minTimeout: 1000,
        maxTimeout: 4000,
      });
      setSessionDead(false);
      close();
    } catch {
      // Still failing after retries: keep the popup and let the user retry
      // manually or log out.
      setReconnectFailed(true);
    } finally {
      setReconnecting(false);
    }
  };

  /** Builds the title/message/buttons matching the current guard state. */
  const buildModalContent = (currentReason: GuardReason): GuardModalContent => {
    if (currentReason === "idle") {
      const isTeacher = userType === UserType.Teacher;
      return {
        title: intl.formatMessage(
          isTeacher ? messages.idleTitleTeacher : messages.idleTitleStudent,
        ),
        message: intl.formatMessage(
          isTeacher
            ? messages.idleDescriptionTeacher
            : messages.idleDescriptionStudent,
        ),
        // Dismissing the idle popup means "I'm still here".
        onClose: close,
        buttons: [
          {
            label: intl.formatMessage(
              isTeacher ? messages.continueTeacher : messages.continueStudent,
            ),
            onClick: close,
            variant: "secondary",
            "data-testid": "session-guard-continue",
          },
        ],
      };
    }

    //Expired token

    const messageKey = reconnecting
      ? messages.reconnectingDescription
      : reconnectFailed
        ? messages.reconnectFailedDescription
        : messages.expiredDescription;
    const reconnectButtonKey = reconnecting
      ? messages.reconnecting
      : reconnectFailed
        ? messages.retry
        : messages.reconnect;
    const reconnectButton = {
      label: intl.formatMessage(reconnectButtonKey),
      onClick: onReconnect,
      variant: "primary",
      disabled: reconnecting,
      "data-testid": "session-guard-reconnect",
    } satisfies ButtonProps;
    const logoutButton = {
      label: intl.formatMessage(messages.logout),
      onClick: goToLogin,
      variant: "secondary",
      disabled: reconnecting,
      "data-testid": "session-guard-logout",
    } satisfies ButtonProps;

    const buttons = reconnectFailed
      ? [reconnectButton, logoutButton]
      : [reconnectButton];
    return {
      title: intl.formatMessage(messages.expiredTitle),
      message: intl.formatMessage(messageKey),
      buttons: buttons,
    };
  };

  return (
    <SessionGuardContext.Provider
      value={{
        reason,
        setExpiredHold,
        isBlocked: () => reason !== undefined,
      }}
    >
      {children}

      {reason !== undefined && (
        <GuardModal reason={reason} {...buildModalContent(reason)} />
      )}
    </SessionGuardContext.Provider>
  );
}

interface GuardModalContent {
  title: string;
  message: string;
  buttons: ButtonProps[];
  /** Omitted = the popup cannot be dismissed. */
  onClose?: () => void;
}

/** Presentational shell of the guard popup; all state lives in the provider. */
function GuardModal({
  reason,
  title,
  message,
  buttons,
  onClose,
}: GuardModalContent & { reason: GuardReason }) {
  return (
    <Modal
      open
      className={`session-guard-modal session-guard-modal--${reason}`}
      onClose={() => onClose?.()}
      title={title}
      aria-labelledby="session-guard-message"
      footerButtons={buttons}
    >
      {message}
    </Modal>
  );
}

export function useSessionGuard(): SessionGuardValue {
  return useContext(SessionGuardContext);
}

const messages = defineMessages({
  idleTitleStudent: {
    id: "exerciseShell-timeoutModalTitle",
    defaultMessage: "Are your still here?",
  },
  idleDescriptionStudent: {
    id: "exerciseShell-timeoutModalDescription",
    defaultMessage:
      "It seems that you have been inactive for a while and we want to make sure that you are still there.",
  },
  continueStudent: {
    id: "exerciseShell-timeoutModalContinueButton",
    defaultMessage: "Continue",
  },
  idleTitleTeacher: {
    id: "exerciseShell-timeoutModalTitleTeacher",
    defaultMessage: "Are your still here?",
  },
  idleDescriptionTeacher: {
    id: "exerciseShell-timeoutModalDescriptionTeacher",
    defaultMessage:
      "It seems that you have been inactive for a while. Are you still there?",
  },
  continueTeacher: {
    id: "exerciseShell-timeoutModalContinueButtonTeacher",
    defaultMessage: "Continue",
  },
  expiredTitle: {
    id: "error-sessionExpiredModal-title",
    defaultMessage: "Votre session a expiré",
  },
  expiredDescription: {
    id: "error-sessionExpiredModal-content",
    defaultMessage:
      "Vous devez vous connecter à nouveau pour pouvoir continuer à utiliser l'application",
  },
  reconnect: {
    id: "error-sessionExpiredModal-relog",
    defaultMessage: "Me reconnecter",
  },
  reconnecting: {
    id: "error-sessionExpiredModal-reconnecting",
    defaultMessage: "Reconnexion…",
  },
  reconnectingDescription: {
    id: "error-sessionExpiredModal-reconnectingContent",
    defaultMessage: "Reconnexion en cours, veuillez patienter…",
  },
  retry: {
    id: "error-sessionExpiredModal-retry",
    defaultMessage: "Réessayer",
  },
  logout: {
    id: "error-sessionExpiredModal-logout",
    defaultMessage: "Se déconnecter",
  },
  reconnectFailedDescription: {
    id: "error-sessionExpiredModal-reconnectFailed",
    defaultMessage:
      "La reconnexion a échoué. Vous pouvez réessayer ou vous déconnecter.",
  },
});

/* ------------------------------------------------------------------------ */
/* Test/dev-only plumbing
/* ------------------------------------------------------------------------ */

/**
 * Test setup used for e2e tests. Injected in window (see
 * e2e/testEventStreamBridge.ts).
 */
export let _sessionGuardTestApi: {
  forceIdle: () => void;
  getState: () => { reason: GuardReason | undefined };
} | null = null;

const useSessionGuardTestApi = (
  reason: GuardReason | undefined,
  forceIdle: () => void,
) => {
  const stateRef = useRef({ reason });
  stateRef.current = { reason };
  const forceIdleRef = useRef(forceIdle);
  forceIdleRef.current = forceIdle;
  useEffect(() => {
    _sessionGuardTestApi = {
      forceIdle: () => forceIdleRef.current(),
      getState: () => stateRef.current,
    };
    return () => {
      _sessionGuardTestApi = null;
    };
  }, []);
};

/**
 * Idle threshold, overridable through localStorage by the dev tools and e2e
 * tests; production always falls through to {@link TIMEOUT}.
 */
const getIdleTimeout = (): number => {
  const timeout: string | undefined = localStorage.getItem(
    localStorage.Key.IDLE_TIMEOUT,
  );
  return timeout ? Number(timeout) : TIMEOUT;
};
